import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Référentiel géographique (pays / villes).
// Les comptes historiques peuvent stocker un CODE pays (pays.codepays,
// ex. « 212 ») ou l'id de la ville (ville.id, ex. « 1 ») au lieu du nom.
// Ces utilitaires résolvent ces valeurs en NOM lisible et fournissent
// les listes déroulantes cohérentes avec l'inscription.
// ─────────────────────────────────────────────────────────────

export interface PaysOption {
  id: number;
  nom_pays: string;
  codepays: number;
}

export interface VilleOption {
  id: number;
  nom_ville: string;
  id_pays: number;
}

export async function loadPays(): Promise<PaysOption[]> {
  const { data } = await supabase
    .from('pays')
    .select('id, nom_pays, codepays')
    .order('nom_pays', { ascending: true });
  return (data as PaysOption[]) || [];
}

export async function loadVilles(): Promise<VilleOption[]> {
  const { data } = await supabase
    .from('ville')
    .select('id, nom_ville, id_pays')
    .order('nom_ville', { ascending: true });
  return (data as VilleOption[]) || [];
}

function isNumericId(value: string | null | undefined): number | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Résout une valeur de pays en NOM lisible.
 * Accepte déjà un nom (« Maroc ») ou un code/id numérique (« 212 »).
 */
export function resolvePaysName(value: string | null | undefined, paysList: PaysOption[]): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const num = isNumericId(raw);
  if (num == null) return raw; // déjà un nom
  const match =
    paysList.find((p) => Number(p.codepays) === num) || paysList.find((p) => Number(p.id) === num);
  return match ? match.nom_pays : raw;
}

/**
 * Résout une valeur de ville en NOM lisible.
 * Accepte déjà un nom (« Fès ») ou un id numérique (« 1 »).
 */
export function resolveVilleName(value: string | null | undefined, villesList: VilleOption[]): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const num = isNumericId(raw);
  if (num == null) return raw; // déjà un nom
  const match = villesList.find((v) => Number(v.id) === num);
  return match ? match.nom_ville : raw;
}

/** Villes rattachées à un pays (par nom de pays). Lien ville.id_pays = pays.codepays. */
export function villesForPays(
  paysName: string | null | undefined,
  paysList: PaysOption[],
  villesList: VilleOption[],
): VilleOption[] {
  const name = (paysName ?? '').trim();
  if (!name) return villesList;
  const pays = paysList.find((p) => p.nom_pays === name);
  if (!pays) return villesList;
  const filtered = villesList.filter(
    (v) => Number(v.id_pays) === Number(pays.codepays) || Number(v.id_pays) === Number(pays.id),
  );
  return filtered.length > 0 ? filtered : villesList;
}